/**
 * AuthPanel.jsx — Authentication panel for dual calendar accounts
 *
 * Displays two account cards — one for Invero (CAL-1) and one for
 * Work 2 (CAL-2). Each card shows:
 *   - Disconnected: Login button with calendar label
 *   - Connecting: Loading spinner
 *   - Connected: User name, email, and disconnect option
 *   - Error: Error message with retry button
 *
 * The panel is the primary UI until both accounts are authenticated,
 * after which the calendar view takes over.
 */

import React from 'react';
import { calendarSources } from '../config/msalConfig';

/**
 * Single account card component.
 * Renders differently based on connection status.
 */
function AccountCard({ source, authState, onLogin, onLogout }) {
  const { status, profile, error } = authState;

  return (
    <div className="account-card" style={{ '--accent': source.color }}>
      <div className="account-card-header">
        <span className="account-dot" style={{ backgroundColor: source.color }} />
        <span className="account-label">{source.label}</span>
        <span className="account-tz">{source.displayCity}</span>
      </div>

      <div className="account-card-body">
        {status === 'disconnected' && (
          <button className="login-btn" onClick={onLogin}>
            Sign in with Microsoft
          </button>
        )}

        {status === 'connecting' && (
          <div className="connecting">
            <div className="spinner" />
            <span>Authenticating...</span>
          </div>
        )}

        {status === 'connected' && profile && (
          <div className="connected-info">
            <div className="user-avatar">
              {profile.displayName.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <span className="user-name">{profile.displayName}</span>
              <span className="user-email">{profile.mail}</span>
            </div>
            <button className="disconnect-btn" onClick={onLogout} title="Disconnect">
              ×
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="error-state">
            <span className="error-msg">{error}</span>
            <button className="retry-btn" onClick={onLogin}>
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main auth panel — renders both account cards.
 */
export default function AuthPanel({ cal1, cal2, loginCal1, loginCal2, logoutCal1, logoutCal2 }) {
  return (
    <div className="auth-panel">
      <div className="auth-panel-header">
        <h2>Connect Your Calendars</h2>
        <p>Sign in to both Microsoft 365 accounts to view your consolidated calendar.</p>
      </div>

      <div className="account-cards">
        <AccountCard
          source={calendarSources.CAL1}
          authState={cal1}
          onLogin={loginCal1}
          onLogout={logoutCal1}
        />
        <AccountCard
          source={calendarSources.CAL2}
          authState={cal2}
          onLogin={loginCal2}
          onLogout={logoutCal2}
        />
      </div>

      {cal1.status === 'connected' && cal2.status === 'connected' && (
        <div className="auth-complete">
          Both calendars connected — loading calendar view...
        </div>
      )}
    </div>
  );
}
